import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  getApiBaseUrl,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  ServiceProviderReceiptUploadControllerBodyTypes,
  ServiceProviderReceiptUploadControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { ServiceProviderReceiptUploadControllerDefinition } from '@pflegenavi/service-provider-shared/api-endpoints';
import { generatePath } from '@pflegenavi/frontend/routing';

export interface IServiceProviderReceiptUploadApi extends Api {
  getReceiptUrls(dataIn: {
    body: ServiceProviderReceiptUploadControllerBodyTypes['getReceiptUrls'];
  }): Promise<
    | ServiceProviderReceiptUploadControllerResultTypes['getReceiptUrls']
    | undefined
  >;

  getUploadUrl(dataIn: {
    params: {
      imageId: string;
    };
    body: ServiceProviderReceiptUploadControllerBodyTypes['getUploadUrl'];
  }): Promise<
    ServiceProviderReceiptUploadControllerResultTypes['getUploadUrl']
  >;
}

export class ServiceProviderReceiptUploadApi
  implements IServiceProviderReceiptUploadApi
{
  baseUrl: string;

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getApiBaseUrl(tenantId, apiUrl);
  }

  // eslint-disable-next-line class-methods-use-this
  get headers(): Headers {
    return new Headers({
      'content-type': 'application/json',
    });
  }

  async getUploadUrl(dataIn: {
    params: {
      imageId: string;
    };
    body: ServiceProviderReceiptUploadControllerBodyTypes['getUploadUrl'];
  }): Promise<
    ServiceProviderReceiptUploadControllerResultTypes['getUploadUrl']
  > {
    const endpoint = generatePath(
      ServiceProviderReceiptUploadControllerDefinition.endpoints.getUploadUrl
        .url,
      { id: dataIn.params.imageId }
    );
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/${ServiceProviderReceiptUploadControllerDefinition.controller}/${endpoint}`,
      {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(dataIn.body),
      }
    );
    return result.text();
  }

  async getReceiptUrls(dataIn: {
    body: ServiceProviderReceiptUploadControllerBodyTypes['getReceiptUrls'];
  }): Promise<
    | ServiceProviderReceiptUploadControllerResultTypes['getReceiptUrls']
    | undefined
  > {
    const endpoint = generatePath(
      ServiceProviderReceiptUploadControllerDefinition.endpoints.getReceiptUrls
        .url
    );

    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/${ServiceProviderReceiptUploadControllerDefinition.controller}/${endpoint}`,
      {
        body: JSON.stringify(dataIn.body),
        method: 'POST',
        headers: this.headers,
      }
    );
    if (result.ok) {
      return await result.json();
    }
    return undefined;
  }
}
